import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Address, GetAddress } from '../../../api/app-types/address.app';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { CreateInstitution } from '../../../api/app-types/institution.app';
import { INSTITUTION_SERVICE, InstitutionService } from '../../../api/services/institution.service';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { RecordManagerService } from '../../../core/services/record-manager.service';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { hasError } from '../../../shared/helpers/has-error.func';
import { wrapPromiseWithNotify } from '../../../shared/helpers/notify.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';

@Component({
	selector: 'lvr-institution-create',
	templateUrl: './institution-create.html',
	imports: [
		LvrPaddingDirective,
		ReactiveFormsModule,
		TranslocoPipe,
		LvrField,
		MatFormFieldModule,
		LvrInputDirective,
		MatInput,
		FormsModule,
		LvrAppendAsteriskPipe,
		AddressCompletion,
		MatRadioGroup,
		MatRadioButton,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrContactPoint,
		MatIcon,
		MatIconButton,
		MatExpansionModule,
		LvrDefaultActions,
		LvrButton,
	],
	providers: [
		RecordManagerService,
		{
			provide: INSTITUTION_SERVICE,
			useClass: InstitutionService,
		},
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
})
export class InstitutionCreate extends RouteInjection {
	private readonly fb = inject(FormBuilder);
	private readonly persistentStorage = inject(FormPersistenceService);
	private readonly recordMngr = inject(RECORD_MANAGER);
	private readonly api = inject(INSTITUTION_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	public readonly env = inject(ENVIRONMENT);
	private readonly session = inject(SessionManagerService);
	private readonly params = toSignal(inject(ActivatedRoute).queryParams);
	private readonly branchRegex = new RegExp(this.env.branchNameRegexStr);
	private readonly contactPointCategoriesData =
		this.resolveDataAsSignal<PaginatedResponse<ContactPointCategory>>('contactPointCategories');
	public readonly contactPointCategories = computed<ContactPointCategory[]>(
		() => this.contactPointCategoriesData()?.entities ?? [],
	);
	public readonly checkDuplicatedAddress = signal(true);
	public readonly saving = signal(false);
	public readonly communicationWayCount = signal(1);
	public readonly communicationWayCountArrayKeys = computed(() => Array(this.communicationWayCount()));

	public readonly draftId = FORM_KEYS.CREATE_INSTITUTION;
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly canCreateInstitutionsPermission = hasPermission(this.session.permissions, 'Institutions.Create');
	public readonly form = this.fb.group({
		name: [this.params()?.['name'] ?? '', [Validators.required, Validators.pattern(this.branchRegex)]],
		industryName: [''],
		address: this.fb.control<GetAddress | undefined>(undefined),
		employmentRequired: this.fb.control<boolean | undefined>(undefined), // DE: Beschäftigungspflichtig / Nicht Beschäftigungspflichtig
		communicationWays: this.fb.control<ContactPoint[]>([], {
			validators: [Validators.max(5)],
		}),
		url: ['', []],
	});

	public duplicatedAddressCheckAsync = async (address: Address, excludeId?: number) => {
		const street = (address.streetAndHouseNumber ?? '').trim();
		const postalCity = (address.postalCodeAndCity ?? '').trim();
		if (!street || !postalCity) return undefined;
		return await this.api.isAddressUnique(street, postalCity, excludeId);
	};

	public cancel() {
		this.persistentStorage.deleteDraft('institution', this.draftId);
		this.recordMngr.closeAndSwitchToNextRecord();
	}

	public async save() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.saving.set(true);
		try {
			const value = this.form.value;
			const body: CreateInstitution = {
				name: value.name?.trim() ?? '',
				address: value.address === null ? undefined : value.address,
				industryName: value.industryName?.trim(),
				employmentRequired: value.employmentRequired === null ? undefined : value.employmentRequired,
				contactPoints: [],
				url: value.url?.trim(),
			};

			const filteredCws = (value.communicationWays ?? []).filter(isContactPointComplete);

			if (filteredCws.length > 0) {
				const additionalCws = filteredCws.map((cp) => ({
					preferredWayOfContact: cp.preferredWayOfContact,
					categoryContactPointId: cp.categoryContactPointId,
					contactValue: cp.contactValue?.trim(),
					remark: cp.remark?.trim(),
				}));

				body.contactPoints!.push(...additionalCws);
			}

			const id = await wrapPromiseWithNotify(
				this.api.create(body),
				this.transloco.translate('base.notification_message.institution_created'),
				this.ntfy,
				this.transloco.translate('base.notification_message.institution_create_error'),
			);
			this.persistentStorage.deleteDraft('institution', this.draftId);
			const active = this.recordMngr.activeRecord();
			if (active?.id) this.recordMngr.closeRecord(active.id);
			setTimeout(() => this.router.navigate(['/desk/institution', id]), 300);
		} finally {
			this.saving.set(false);
		}
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = (ctrl.value ?? []) as ContactPoint[];
		if (data.valid) {
			if (index >= 0 && index < values.length) {
				values[index] = data.value;
			} else {
				values.push(data.value);
			}
		} else {
			if (index >= 0 && index < values.length) {
				values.splice(index, 1);
			}
		}

		ctrl.setValue([...values]);
	}

	public addCommunicationWay() {
		this.communicationWayCount.set(this.communicationWayCount() + 1);
	}

	public removeCommunicationWay(id: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const index = values.findIndex((v) => v.id === id);
		if (index !== -1) {
			values.splice(index, 1);
			ctrl.setValue(values);
		}
		this.communicationWayCount.set(this.communicationWayCount() - 1);
	}
}
